/**
 * Note Metadata Encoding/Decoding
 *
 * Encodes note type + tags into the annotation subject field.
 * Format: [NoteType] tag1,tag2 | User-visible subject
 */

import { NOTE_TYPES, NOTE_TAGS, type NoteType, type NoteTag, type NoteMetadata } from '../model/types';

const NOTE_TYPE_SET = new Set<string>(NOTE_TYPES);
const NOTE_TAG_SET = new Set<string>(NOTE_TAGS);

/**
 * Encode note type + tags into the subject field.
 * Format: "[NoteType] tag1,tag2 | User subject"
 */
export function encodeNoteSubject(
  noteType: NoteType,
  tags: NoteTag[],
  userSubject?: string,
): string {
  const parts: string[] = [`[${noteType}]`];

  if (tags.length > 0) {
    parts.push(tags.join(','));
  }

  if (userSubject?.trim()) {
    parts.push('|');
    parts.push(userSubject.trim());
  }

  const result = parts.join(' ');
  return result.length > 500 ? result.slice(0, 500) : result;
}

/**
 * Decode structured metadata from a subject string.
 * Returns null if the subject doesn't match the expected format.
 */
export function decodeNoteSubject(subject: string | undefined): NoteMetadata | null {
  if (!subject) return null;

  const match = /^\[([^\]]+)\]\s*(.*)$/.exec(subject);
  if (!match) return null;

  const rawType = match[1]!;
  if (!NOTE_TYPE_SET.has(rawType)) return null;

  const noteType = rawType as NoteType;
  const remainder = match[2]!.trim();

  const pipeIndex = remainder.indexOf('|');
  let tagsStr: string;
  let userSubject: string;

  if (pipeIndex >= 0) {
    tagsStr = remainder.slice(0, pipeIndex).trim();
    userSubject = remainder.slice(pipeIndex + 1).trim();
  } else {
    tagsStr = remainder;
    userSubject = '';
  }

  const tags = tagsStr
    ? tagsStr.split(',').map(t => t.trim()).filter(t => NOTE_TAG_SET.has(t)) as NoteTag[]
    : [];

  return { noteType, tags, userSubject };
}
